import { useId, useRef } from 'react';
import { Bold, Heading, Italic, Link as LinkIcon, List } from 'lucide-react';
import { t } from '../../lib/i18n';
import { parseNoticeText } from '@cloudsign/shared';
import { NoticeView } from './noticeText';

type Insert = { before: string; after: string; sample: string; linePrefix?: boolean };

/** Body textarea with a toolbar that inserts the Markdown subset, and a live preview. */
export function NoticeTextEditor({
  value,
  onChange,
  label,
  disabled = false,
  error,
  rows = 10,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  disabled?: boolean;
  error?: string | undefined;
  rows?: number;
}) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);

  const apply = (ins: Insert) => {
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const picked = value.slice(start, end) || ins.sample;
    let next: string;
    if (ins.linePrefix) {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      next = `${value.slice(0, lineStart)}${ins.before}${value.slice(lineStart, start)}${picked}${value.slice(end)}`;
    } else {
      next = `${value.slice(0, start)}${ins.before}${picked}${ins.after}${value.slice(end)}`;
    }
    onChange(next);
    requestAnimationFrame(() => el?.focus());
  };

  const tools: { key: string; label: string; icon: typeof Bold; ins: Insert }[] = [
    { key: 'bold', label: t('fnUi.editor.bold'), icon: Bold, ins: { before: '**', after: '**', sample: t('fnUi.editor.sampleBold') } },
    { key: 'italic', label: t('fnUi.editor.italic'), icon: Italic, ins: { before: '*', after: '*', sample: t('fnUi.editor.sampleItalic') } },
    { key: 'heading', label: t('fnUi.editor.heading'), icon: Heading, ins: { before: '## ', after: '', sample: t('fnUi.editor.sampleHeading'), linePrefix: true } },
    { key: 'list', label: t('fnUi.editor.list'), icon: List, ins: { before: '- ', after: '', sample: t('fnUi.editor.sampleItem'), linePrefix: true } },
    { key: 'link', label: t('fnUi.editor.link'), icon: LinkIcon, ins: { before: '[', after: '](https://)', sample: t('fnUi.editor.sampleLink') } },
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-800">
          {label}
        </label>
        <div role="toolbar" aria-label={t('fnUi.editor.toolbar')} className="mb-2 flex gap-1">
          {tools.map((x) => (
            <button
              key={x.key}
              type="button"
              disabled={disabled}
              aria-label={x.label}
              title={x.label}
              onClick={() => apply(x.ins)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <x.icon aria-hidden="true" className="h-4 w-4" />
            </button>
          ))}
        </div>
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={`${id}-hint`}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 hover:border-slate-400 disabled:bg-slate-100 aria-[invalid=true]:border-red-600"
        />
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-600">
          {error ?? t('fnUi.editor.bodyHint')}
        </p>
      </div>
      <div>
        <h3 className="mb-1 text-sm font-medium text-slate-800">{t('fnUi.editor.preview')}</h3>
        <div aria-label={t('fnUi.editor.preview')} role="region" className="min-h-[8rem] rounded-md border border-slate-200 bg-slate-50 p-4">
          {parseNoticeText(value).length ? <NoticeView body={value} /> : <p className="text-sm text-slate-600">{t('fnUi.editor.previewEmpty')}</p>}
        </div>
      </div>
    </div>
  );
}
